<template>
  <div>
    <div class="mt-2 d-flex" style="gap: 12px">
      <v-text-field
        v-model="search"
        label="Search expenses..."
        prepend-inner-icon="mdi-magnify"
        clearable
        dense
        outlined
        style="max-width: 320px"
      />
      <v-select
        v-model="selectedCategories"
        :items="categoryList"
        item-text="description"
        item-value="id"
        label="Categories"
        multiple
        dense
        outlined
        clearable
        hide-details
        style="max-width: 360px"
      >
        <template #prepend-item>
          <v-list-item ripple @click="toggleAllCategories">
            <v-list-item-action>
              <v-icon :color="selectedCategories.length ? 'primary' : ''">
                {{ allCategoriesSelected ? 'mdi-checkbox-marked' : selectedCategories.length ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline' }}
              </v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Select All</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mt-1" />
        </template>
        <template #selection="{ item, index }">
          <span v-if="allCategoriesSelected && index === 0">All Categories</span>
          <span v-else-if="!allCategoriesSelected">
            <span v-if="index === 0">{{ item.description }}</span>
            <span v-if="index === 1" class="grey--text text-caption ml-1">(+{{ selectedCategories.length - 1 }} more)</span>
          </span>
        </template>
      </v-select>
    </div>
    <base-snackbar :errors.sync="expenseGetErrors" />
    <v-data-table
      :items="expenseList"
      :loading="expenseGetting"
      :headers="headers"
      :items-per-page.sync="itemsPerPage"
      :page.sync="page"
      :server-items-length="expenseGetCount"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ (item.category || {}).description || 'N/A' }}</td>
          <td>{{ item.description }}</td>
          <td>{{ Number(item.cost || 0).phpFormat() }}</td>
          <td>{{ (item.date || '').dateFormat() }}</td>
          <td>
            <base-tooltip
              v-if="authPermissions.has('expenses:edit')"
              color="warning"
              tooltip="edit"
              dark="dark"
              x-small="x-small"
              text="text"
              bottom="bottom"
              @click="editRecord(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </base-tooltip>
            <base-tooltip
              v-if="authPermissions.has('expenses:edit')"
              color="error"
              tooltip="delete"
              dark="dark"
              x-small="x-small"
              text="text"
              bottom="bottom"
              :loading="expenseDeleting"
              @click="deleteRecord(item.id)"
            >
              <v-icon>mdi-delete</v-icon>
            </base-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <edit-expenses
      v-model="editDialog"
      :expense="editingExpense"
      :categories="categoryList"
      @update-success="fetchExpenses"
    />
  </div>
</template>
<script>
import VueRequestHandler from '@/classes/VueRequestHandler.class'
import requestVars from '@/extra-functions/request-vars.extra'
import expenseRepository from '@/repositories/expense.repository'
import expenseCategoryRepository from '@/repositories/expense-category.repository'
import { mapState } from 'vuex'
import EditExpenses from './Edit.vue'

const [expenseGetVars, expenseGetVarNames] = requestVars({
  identifier: 'expense',
  pagination: true,
})
const [expenseDeleteVars, expenseDeleteVarNames] = requestVars({
  identifier: 'expense',
  request: 'delete',
  hasData: false,
})
const expenseGetHandler = new VueRequestHandler(
  null,
  expenseGetVarNames,
  {},
  true,
)
const expenseDeleteHandler = new VueRequestHandler(null, expenseDeleteVarNames)

const tableVars = () => ({
  headers: [
    { text: 'Category', value: 'expense_category_id' },
    { text: 'Description', value: 'description' },
    { text: 'Cost', value: 'cost' },
    { text: 'Date', value: 'date' },
    { text: 'Config', sortable: false },
  ],
  page: 1,
  itemsPerPage: 10,
  search: '',
  categoryList: [],
  selectedCategories: [],
  editDialog: false,
  editingExpense: {},
})

export default {
  name: 'TableExpenses',
  components: {
    EditExpenses,
  },
  data: () => ({
    ...expenseGetVars,
    ...expenseDeleteVars,
    ...tableVars(),
    websocketEvents: [],
  }),
  watch: {
    page() {
      this.fetchExpenses()
    },
    itemsPerPage() {
      this.page = 1
      this.fetchExpenses()
    },
    search() {
      this.page = 1
      this.fetchExpenses()
    },
    selectedCategories() {
      this.page = 1
      this.fetchExpenses()
    },
  },
  created() {
    this.fetchCategories()
    this.initWebsocketEvents()
  },
  beforeDestroy() {
    this.websocketEvents.forEach((eventId) => {
      this.$store.dispatch('websocket/detachFromListener', [eventId])
    })
    this.websocketEvents = []
  },
  computed: {
    ...mapState({
      authPermissions: (state) => state.auth.userPermissions,
    }),
    allCategoriesSelected() {
      return this.categoryList.length > 0 && this.selectedCategories.length === this.categoryList.length
    },
  },
  methods: {
    deleteRecord(id) {
      this.$confirm('are you sure you want to remove this record?').then(
        (confirm) => {
          if (!confirm) return
          const handler = expenseDeleteHandler
          const repository = expenseRepository.delete
          handler.setVue(this)
          handler.execute(repository, [id])
        },
      )
    },
    editRecord(item) {
      this.editingExpense = item
      this.editDialog = true
    },
    async initWebsocketEvents() {
      const dbStoreEventId = await this.$store.dispatch(
        'websocket/attachToListener',
        ['DBStoreEvent', this.dbStoreEvent],
      )
      const dbUpdateEventId = await this.$store.dispatch(
        'websocket/attachToListener',
        ['DBUpdateEvent', this.dbUpdateEvent],
      )
      this.websocketEvents.push(dbStoreEventId, dbUpdateEventId)
    },
    dbStoreEvent({ data, model }) {
      if (!model || this.$objectEmpty(data)) return
      if (model === 'Expense') {
        this.expenseDBStoreEvent()
      }
    },
    expenseDBStoreEvent() {
      this.fetchExpenses()
    },
    dbUpdateEvent({ data, model }) {
      if (!model || this.$objectEmpty(data)) return
      if (model === 'Expense') {
        this.fetchExpenses()
      }
    },
    dbDeleteEvent({ id, model }) {
      if (!id || !model) return
      if (model === 'Expense') {
        this.expenseDBDeleteEvent(id)
      }
    },
    expenseDBDeleteEvent(id) {
      this.expenseList = this.expenseList.filter(
        (item) => item.id !== Number(id || -1),
      )
    },
    fetchExpenses() {
      const handler = expenseGetHandler
      const repository = expenseRepository.index
      const params = this.getGetExpenseParams()
      handler.setVue(this)
      handler.execute(repository, [params])
    },
    async fetchCategories() {
      try {
        const { data } = await expenseCategoryRepository.index({ storeId: this.$store.state.settings.storeId })
        this.categoryList = data
        this.selectedCategories = data.map(c => c.id)
        this.fetchExpenses()
      } catch (e) {
        console.error(e)
        this.fetchExpenses()
      }
    },
    toggleAllCategories() {
      if (this.allCategoriesSelected) {
        this.selectedCategories = []
      } else {
        this.selectedCategories = this.categoryList.map(c => c.id)
      }
    },
    getGetExpenseParams() {
      return {
        with: 'category',
        offset: (this.page - 1) * this.itemsPerPage,
        limit: this.itemsPerPage,
        storeId: this.$store.state.settings.storeId,
        search: this.search,
        categories: this.selectedCategories,
      }
    },
  },
}
</script>
